// Public-profile publication policy.
//
// Private user documents never cross the owner seam. Consumers outside the
// owner profile receive only the projection produced here, so rendering and
// search cannot accidentally recover a field that its owner hid.
import publicationPolicy from "./profilePublicationPolicy.json";

export const PROFILE_PRIVACY_FLAGS = Object.freeze({
  ...publicationPolicy.privacyFlags,
});

export const ALWAYS_PUBLISHED_PROFILE_FIELDS = Object.freeze([
  ...publicationPolicy.alwaysPublishedFields,
]);

export const PUBLISHED_PROFILE_FIELDS = Object.freeze([
  ...ALWAYS_PUBLISHED_PROFILE_FIELDS,
  ...Object.keys(PROFILE_PRIVACY_FLAGS),
]);

export const PUBLISHED_PROFILE_SEARCH_FIELDS = Object.freeze([
  ...publicationPolicy.searchFields,
]);

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value || {}, key);
}

/** A gated field is public unless its owner explicitly turned it off. */
export function isProfileFieldPublished(profile, field) {
  const flag = PROFILE_PRIVACY_FLAGS[field];
  if (!flag) return ALWAYS_PUBLISHED_PROFILE_FIELDS.includes(field);
  return profile?.privacy_settings?.[flag] !== false;
}

/**
 * Project a private users/{uid} document into the only shape other users may
 * consume. Unknown keys, phone numbers, timestamps, and privacy configuration
 * are deliberately dropped.
 */
export function publishProfile(profile, uid = profile?.uid || profile?.id) {
  if (!profile) return null;

  const published = {};
  if (uid) published.uid = String(uid);

  for (const field of PUBLISHED_PROFILE_FIELDS) {
    if (field === "uid" || !isProfileFieldPublished(profile, field)) continue;
    if (hasOwn(profile, field) && profile[field] !== undefined) {
      published[field] = profile[field];
    }
  }

  return published;
}

/** Read one permitted value from either a private or already-published shape. */
export function publishedProfileValue(profile, field) {
  if (!profile || !isProfileFieldPublished(profile, field)) return undefined;
  return profile[field];
}

/**
 * Do two published profiles carry the same visible values?
 *
 * Compares only the policy's fields, so a differing `published_at` timestamp or
 * object identity does not read as a change. Overlaying a live profile that is
 * equal to the one already in hand would churn every downstream memo.
 */
export function samePublishedProfile(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return PUBLISHED_PROFILE_FIELDS.every(
    (field) => (a[field] ?? "") === (b[field] ?? "")
  );
}

/** Search only the values that the publication policy permits. */
export function matchesPublishedProfile(profile, query) {
  if (!profile || !query?.trim()) return false;
  const published = publishProfile(profile);
  const needle = query.trim().toLocaleLowerCase();
  return PUBLISHED_PROFILE_SEARCH_FIELDS.some((field) =>
    String(published?.[field] ?? "").toLocaleLowerCase().includes(needle)
  );
}
